import React, { useEffect, useRef, useState } from 'react';

// Resting pupil offsets, expressed against a 204px reference eye, mirroring a
// hand-drawn character: left eye rests up-left, right eye rests down-right.
const REF_EYE = 204;
const RAW = {
  left: { x: 74.8272 - 102, y: 78.3184 - 102 },
  right: { x: 119.032 - 102, y: 117.787 - 102 },
};

export default function Hero() {
  const leftRef = useRef(null);
  const rightRef = useRef(null);
  const [pos, setPos] = useState({ left: RAW.left, right: RAW.right });

  useEffect(() => {
    const scaled = (ref, raw) => {
      const w = ref.current ? ref.current.getBoundingClientRect().width : REF_EYE;
      const s = w / REF_EYE;
      return { x: raw.x * s, y: raw.y * s };
    };

    let defaults = {
      left: scaled(leftRef, RAW.left),
      right: scaled(rightRef, RAW.right),
    };
    setPos(defaults);

    // Note: no prefers-reduced-motion guard here on purpose — the pupil follow
    // is a direct 1:1 cursor response with no transition/easing/looping, and
    // Safari reports reduce-motion whenever macOS/iOS Low Power Mode is on,
    // which would silently freeze the eyes for a large share of visitors.

    const compute = (ref, def, e) => {
      if (!ref.current) return def;
      const r = ref.current.getBoundingClientRect();
      const cx = r.left + r.width / 2;
      const cy = r.top + r.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < 1) return def;
      // eyeRadius - pupilRadius - small buffer, relative to live rendered size
      const maxMove = r.width * (0.5 - 0.186 - 0.02);
      const nx = dx / dist;
      const ny = dy / dist;
      let mx = Math.min(dist, maxMove) * nx + def.x;
      let my = Math.min(dist, maxMove) * ny + def.y;
      const total = Math.hypot(mx - def.x, my - def.y);
      if (total > maxMove) {
        const k = maxMove / total;
        mx = def.x + (mx - def.x) * k;
        my = def.y + (my - def.y) * k;
      }
      return { x: mx, y: my };
    };

    const onMove = (e) => {
      setPos({
        left: compute(leftRef, defaults.left, e),
        right: compute(rightRef, defaults.right, e),
      });
    };
    const onResize = () => {
      defaults = {
        left: scaled(leftRef, RAW.left),
        right: scaled(rightRef, RAW.right),
      };
    };

    window.addEventListener('mousemove', onMove);
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  const pupilStyle = (p) => ({
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '37%',
    aspectRatio: '1',
    borderRadius: '50%',
    background: '#000000',
    transform: `translate(-50%, -50%) translate(${p.x}px, ${p.y}px)`,
  });

  return (
    <section
      id="top"
      className="grid grid-cols-1 items-center gap-8 py-[clamp(48px,11vw,112px)] md:grid-cols-[minmax(0,7fr)_minmax(0,5fr)] md:gap-[clamp(24px,5vw,72px)]"
      aria-labelledby="home-heading"
    >
      <div>
        <span className="mb-3.5 block text-[13px] uppercase tracking-[0.08em] text-[#FBF0DC]/80">
          Helsinki, Finland
        </span>
        <h1
          id="home-heading"
          className="m-0 font-extrabold leading-[1.05] tracking-[-0.02em] text-[clamp(40px,5.6vw,72px)]"
        >
          Software Developer
          <span className="mt-3 block text-[clamp(19px,2.4vw,28px)] font-normal tracking-[-0.01em] text-[#FBF0DC]/60">
            with an eye for design
          </span>
        </h1>
        <p className="mt-7 max-w-[52ch] text-lg leading-7 text-[#FBF0DC]/[0.78]">
          I'm a software developer in Helsinki who builds web and mobile apps with
          React, React Native, Swift and Python. My UX background means I care as
          much about how a product feels to use as how it's built.
        </p>
        <div className="mt-7 flex flex-wrap gap-4">
          <a href="#it-projects">
            <button type="button" className="btn btn-primary">View IT Projects</button>
          </a>
          <a href="#ux-projects">
            <button type="button" className="btn btn-secondary">View UX Work</button>
          </a>
        </div>
      </div>

      <div className="w-full bg-[#FBF0DC] p-5">
        <div
          className="flex items-center justify-center gap-[8%] bg-[#0062AD] p-[12%]"
          style={{ aspectRatio: '4 / 3.4' }}
        >
          <div
            ref={leftRef}
            className="relative w-[26%] shrink-0 overflow-hidden rounded-full bg-[#FBF0DC]"
            style={{ aspectRatio: '1' }}
          >
            <div style={pupilStyle(pos.left)} />
          </div>
          <div
            ref={rightRef}
            className="relative w-[26%] shrink-0 overflow-hidden rounded-full bg-[#FBF0DC]"
            style={{ aspectRatio: '1' }}
          >
            <div style={pupilStyle(pos.right)} />
          </div>
        </div>
        <div className="px-1 pb-1 pt-6 text-black">
          <h2 className="m-0 mb-1.5 font-extrabold tracking-[-0.01em] text-[clamp(24px,3vw,34px)] text-black">
            Liuwei Luo
          </h2>
          <p className="m-0 mb-6 text-[15px] text-black">Software Developer</p>
          <p className="m-0 text-[13px] uppercase tracking-[0.06em] text-black">Helsinki, Finland</p>
        </div>
      </div>
    </section>
  );
}
